import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useDeleteTrunk, useTrunksList } from '../api';
import type { TrunkRow } from '../types';

export function TrunkListPage() {
  const navigate = useNavigate();
  const query = useTrunksList();
  const rows = query.data ?? [];
  const canCreate = usePermission('Manage Trunks.Create Trunk', BASE_PERMISSION.ADD);
  const canEdit = usePermission('Manage Trunks.Update Trunk', BASE_PERMISSION.EDIT);
  const canDelete = usePermission('Manage Trunks.Delete Trunk', BASE_PERMISSION.DELETE);

  const [pendingDelete, setPendingDelete] = useState<TrunkRow | null>(null);
  const deleteMutation = useDeleteTrunk();

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.trunkId, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted "${pendingDelete.trunkName}"`);
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the trunk'),
    });
  };

  const columns = useMemo<ColumnDef<TrunkRow>[]>(
    () => [
      {
        accessorKey: 'trunkId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.trunkId}</span>
        ),
      },
      {
        accessorKey: 'trunkName',
        header: 'Trunk',
        cell: ({ row }) => (
          <Link
            to={`/update-trunk/${row.original.trunkId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.trunkName}
          </Link>
        ),
      },
      {
        accessorKey: 'pilotNumber',
        header: 'Pilot number',
        cell: ({ row }) => row.original.pilotNumber,
      },
      {
        accessorKey: 'channels',
        header: 'Channels',
        size: 110,
        cell: ({ row }) => <span className="tabular-nums">{row.original.channels}</span>,
      },
      {
        accessorKey: 'cps',
        header: 'CPS',
        size: 100,
        cell: ({ row }) => <span className="tabular-nums">{row.original.cps}</span>,
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => !canEdit && !canDelete ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{row.original.trunkName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canEdit && (
                <DropdownMenuItem
                  onClick={() => navigate(`/update-trunk/${row.original.trunkId}`)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => setPendingDelete(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate, canEdit, canDelete],
  );

  return (
    <FillPage>
      <PageHeader
        title="Trunks"
        description="SIP trunks — carrier connections that route outbound calls."
        actions={
          canCreate ? (
            <Button asChild>
              <Link to="/create-trunk">
                <Plus className="h-4 w-4" />
                Add trunk
              </Link>
            </Button>
          ) : undefined
        }
      />
      <DataTable<TrunkRow, unknown>
        tableId="trunks:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search trunks…"
        emptyTitle="No trunks yet"
        emptyDescription="Add a SIP trunk to enable outbound calling."
        getRowId={(row) => String(row.trunkId)}
        exportFilename="trunks.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            <span className="font-medium text-foreground">{pendingDelete?.trunkName}</span> will be
            permanently deleted. Dialers using this trunk may stop routing calls.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </FillPage>
  );
}
